import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  inject,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { DialogService, ToastService } from '@core';
import { MeterService } from '@feature/meter';
import { Observable } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { GroupBaselineService } from '../data-access/group-baseline.service';
import { APP_ROUTES_CONFIG, PackageType } from '@contracts';

@Component({
  selector: 'app-edit-group-baseline',
  templateUrl: './edit-group-baseline.component.html',
  styleUrls: ['./edit-group-baseline.component.scss'],
})
export class EditGroupBaselineComponent implements OnInit {
  config = inject(APP_ROUTES_CONFIG);
  package: PackageType = this.config.package ?? 'standard';
  separatorKeysCodes: number[] = [ENTER, COMMA];
  groupBaselineCtrl = new FormControl('');
  filteredGroupBaseline: Observable<any[]>;
  groupBaselines: any[] = [];
  allGroupBaseline: any[] = [];

  emails: string[] = [];
  emailCtrl = new FormControl('');

  @ViewChild('fruitInput') fruitInput!: ElementRef<HTMLInputElement>;

  formData = new FormGroup({
    name: new FormControl('', Validators.required),
    setpoint_day: new FormControl(),
    setpoint_month: new FormControl(),
    sensor_id: new FormControl(),
  });

  constructor(
    private rest: GroupBaselineService,
    public dialogRef: MatDialogRef<EditGroupBaselineComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    public dialogService: DialogService,
    private meterService: MeterService,
    public toast: ToastService,
  ) {
    this.formData.patchValue({
      name: data.name,
      setpoint_day: data.setpoint_day,
      setpoint_month: data.setpoint_month,
      sensor_id: data.sensor_id,
    });

    this.emails = Array.isArray(data.emails) ? [...data.emails] : [];
    this.filteredGroupBaseline = this.groupBaselineCtrl.valueChanges.pipe(
      startWith(null),
      map((fruit: string | null) =>
        fruit ? this._filter(fruit) : this.allGroupBaseline.slice(),
      ),
    );
  }

  ngOnInit(): void {
    this.loadMeters();
  }

  loadMeters() {
    this.dialogService
      .withLoading(
        this.meterService.getMeters({
          sensor_type: 'energy',
          sensor_module: 'EMS',
          // get_mdb: true,
        }),
      )
      .subscribe({
        next: (res) => {
          try {
            if (res.meta.response_code == 20000) {
              this.allGroupBaseline = res.meta.response_data;

              if (this.data.sensor_id?.length) {
                this.groupBaselines = this.allGroupBaseline.filter(
                  (item: any) => this.data.sensor_id.includes(item.id),
                );
              }

              this.groupBaselineCtrl.patchValue('');
            } else {
              this.toast.error('โหลดข้อมูลไม่สำเร็จ');
            }
          } catch (error) {
            console.error(`ERROR : [api_get_meter_overview] `, error);
          }
        },
        error: (e) => {
          console.error(e);
          this.toast.error('โหลดข้อมูลไม่สำเร็จ');
        },
      });
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.groupBaselines.push(value);
    }

    event.chipInput!.clear();

    this.groupBaselineCtrl.setValue(null);
  }

  remove(fruit: any): void {
    const index = this.groupBaselines.indexOf(fruit);
    if (index >= 0) {
      this.groupBaselines.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    // this.groupBaselines.push(event.option.viewValue);
    this.groupBaselines.push(event.option.value);
    this.fruitInput.nativeElement.value = '';
    this.groupBaselineCtrl.setValue(null);
  }

  private _filter(value: any): string[] {
    let filterValue = '';
    if (value.name) {
      filterValue = value.name.toLowerCase();
    } else {
      filterValue = value.toLowerCase();
    }
    return this.allGroupBaseline.filter((fruit) => {
      try {
        return fruit.name.toLowerCase().includes(filterValue);
      } catch (error) {
        return;
      }
    });
  }

  confirm() {
    if (this.formData.valid) {
      this.dialogService
        .confirm({
          title: `ต้องการแก้ไข Baseline "${this.formData.value.name}" หรือไม่`,
        })
        .pipe(
          filter(Boolean),
          switchMap(() => {
            const data = {
              name: this.formData.value.name || '',
              setpoint_day: this.formData.value.setpoint_day,
              setpoint_month: this.formData.value.setpoint_month,
              sensor_id: Array.from(
                new Set(this.groupBaselines.map((value) => value.id)),
              ),
              id: this.data.id,
              emails: this.emails,
            };

            return this.dialogService.withLoading(
              this.rest.updateBaselineGroup(data),
            );
          }),
        )
        .subscribe({
          next: (res) => {
            if (res) {
              this.toast.success('แก้ไขข้อมูลสำเร็จ');

              this.dialogRef.close({
                status: true,
                value: res,
              });
            } else {
              this.toast.error('แก้ไขข้อมูลไม่สำเร็จ');
            }
          },
          error: () => {
            this.toast.error('แก้ไขข้อมูลไม่สำเร็จ');
          },
        });
    }
  }

  addEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (!value) {
      return;
    }

    // กัน email ซ้ำ
    if (!this.emails.includes(value)) {
      this.emails.push(value);
    }

    event.chipInput?.clear();
    this.emailCtrl.setValue('');
  }

  removeEmail(email: string): void {
    const index = this.emails.indexOf(email);

    if (index >= 0) {
      this.emails.splice(index, 1);
    }
  }
}
